import type { SportFilterMode } from '@/lib/kalshiTicker/sportFilter';
import { parseTicker } from '@/lib/kalshiTicker';

// Market-family filter (Prof, Jul 7): a second chip line below the sport
// chips — moneyline / spread / total. Combines with the sport filter as an
// AND (Soccer + Total = soccer totals only). Spread and total classify from
// the parsed ticker; EVERYTHING else (game moneylines, advance, series,
// props, poly tokens) buckets as 'moneyline' so the three chips partition
// the whole list and no row disappears into an unclassifiable bucket.
//
// Extracted from index.tsx so the filter-persistence module can validate
// restored kind keys against MARKET_KIND_CHIPS without importing index.tsx
// (which would form an import cycle).
export type MarketKind = 'moneyline' | 'spread' | 'total';

export const MARKET_KIND_CHIPS: ReadonlyArray<{ id: MarketKind; label: string }> = [
  { id: 'moneyline', label: 'Moneyline' },
  { id: 'spread', label: 'Spread' },
  { id: 'total', label: 'Total' },
];

export type MarketKindFilter = ReadonlyMap<MarketKind, SportFilterMode>;

/**
 * Classify a ticker into its market family.
 *
 * Moved here from `index.tsx` by #539 for the same reason the types above were
 * extracted: a SECOND consumer outside Position Tracker now needs it, and
 * importing `index.tsx` to get it would drag a 6k-line view (and an import
 * cycle) behind a pure string classification. The Value Dashboard uses it to
 * decide which board a focused deep link belongs to.
 *
 * `index.tsx` re-exports it, so every existing caller and test is unaffected.
 *
 * Reads structured `spread`/`total` info first, then the category suffix as the
 * fallback for outcomes the sport parsers recognize but don't fully
 * destructure — which is why CFB's `cfb-spread` / `cfb-total` categories (#532)
 * classify correctly with no CFB-specific case here.
 *
 * Exported for tests.
 */
export function marketKindFromTicker(ticker: string): MarketKind {
  const parsed = parseTicker(ticker);
  // Structured spread/total info first, category suffix as the fallback for
  // outcomes the sport parsers recognize but don't fully destructure.
  if (parsed.spread || parsed.category.endsWith('-spread')) return 'spread';
  if (parsed.total || parsed.category.endsWith('-total')) return 'total';
  return 'moneyline';
}
